

import { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

export const useCart = () => {
    const [cartLoading, setCartLoading] = useState(false);
    const [quantities, setQuantities] = useState({});

    // ✅ Auth helper
    const getAuthVars = () => {
        const userId = localStorage.getItem("userId");
        const token = localStorage.getItem("authToken");
        return { userId, token };
    };

    // ✅ Common ID getter
    const getItemKey = (item) => item?.cartItemId ?? item?.itemId;

    // ✅ ADD TO CART
    const addToCart = async (dish, quantity, seatingId, fetchCartDetails) => {
        const { userId, token } = getAuthVars();

        if (!userId || !token) {
            toast.error("Please login first");
            return false;
        }

        if (!seatingId) {
            toast.error("Select table first");
            return false;
        }

        setCartLoading(true);

        try {
            const res = await axios.post(
                `https://backend.maamaas.com/food/api/cart/add/item`,
                { dishId: dish.dishId, quantity },
                {
                    params: {
                        userId: Number(userId),
                        seatingId: Number(seatingId)
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (res.status === 200 || res.status === 201) {
                toast.success(`${dish.dishName} added to cart`);
                if (fetchCartDetails) await fetchCartDetails();
                return true;
            }
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Add to cart failed";

            toast.error(msg);
            return false;
        } finally {
            setCartLoading(false);
        }
    };

    // ✅ REMOVE ITEM
    const removeCartItem = async (item, fetchCartDetails) => {
        const { userId, token } = getAuthVars();
        const itemId = getItemKey(item);

        if (!itemId) return;

        try {
            const res = await axios.delete(
                `https://backend.maamaas.com/food/api/cart/remove/item`,
                {
                    params: {
                        userId: Number(userId),
                        itemId: Number(itemId)
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (res.status === 200) {
                toast.success(`${item.dishName} removed`);
                if (fetchCartDetails) await fetchCartDetails();
            }
        } catch {
            toast.error("Remove failed");
        }
    };

    // ✅ UPDATE ITEM (IMPORTANT FIX)
    const updateCartItem = async (item, newQuantity) => {
        const { userId, token } = getAuthVars();
        const itemId = getItemKey(item);

        try {
            return await axios.put(
                `https://backend.maamaas.com/food/api/cart/update/item`,
                {},
                {
                    params: {
                        userId: Number(userId),
                        itemId: Number(itemId),
                        quantity: Number(newQuantity) // ✅ REQUIRED PARAM FIX
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
        } catch (err) {
            console.log("UPDATE ERROR:", err?.response?.data);
            throw err;
        }
    };

    // ✅ INCREMENT
    const incrementQuantity = async (item, fetchCartDetails) => {
        const itemId = getItemKey(item);
        if (!itemId) return;

        const currentQty = quantities[itemId] || item.quantity || 1;
        const newQty = currentQty + 1;

        try {
            const res = await updateCartItem(item, newQty);

            if (res.status === 200) {
                setQuantities(prev => ({
                    ...prev,
                    [itemId]: newQty
                }));

                if (fetchCartDetails) await fetchCartDetails();
            }
        } catch {
            toast.error("Update failed");
        }
    };

    // ✅ DECREMENT
    const decrementQuantity = async (item, fetchCartDetails) => {
        const itemId = getItemKey(item);
        if (!itemId) return;

        const currentQty = quantities[itemId] || item.quantity || 1;

        // 👉 If 1 → remove item
        if (currentQty === 1) {
            await removeCartItem(item, fetchCartDetails);
            return;
        }

        const newQty = currentQty - 1;

        try {
            const res = await updateCartItem(item, newQty);

            if (res.status === 200) {
                setQuantities(prev => ({
                    ...prev,
                    [itemId]: newQty
                }));

                if (fetchCartDetails) await fetchCartDetails();
            }
        } catch {
            toast.error("Update failed");
        }
    };

    // ✅ SEND TO CHEF
    const handleSendToChef = async (item, fetchCartDetails) => {
        try {
            const itemId = getItemKey(item);
            const qty = quantities[itemId] || item.quantity || 1;

            const res = await updateCartItem(item, qty);

            if (res.status === 200) {
                toast.success(`${item.dishName} sent to kitchen`);
                if (fetchCartDetails) await fetchCartDetails();
            }
        } catch {
            toast.error("Send failed");
        }
    };

    return {
        cartLoading,
        quantities,
        setQuantities,
        addToCart,
        removeCartItem,
        incrementQuantity,
        decrementQuantity,
        handleSendToChef
    };
};